import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/Ionicons';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { ICommunityResourceFile } from '@/types/residents/communityResource';
import { openExternalUrl } from '@/utils/common/linking';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { FileRow } from '@/components/residents/resources/FileRow';
import { getCommunityResourceIcon, CommunityResourceIconColors } from '@/constants/residents/communityResourceIcons';
import { showAlert } from '@/utils/common/alert';
import {
  PADDING,
  CARD_STYLES,
  TYPOGRAPHY,
  ICON_SIZES,
} from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { homeStrings } from '@/constants/residents/strings';

type ResourceDetailRouteProp = RouteProp<RootStackParamList, 'ResourceDetail'>;

export const ResourceDetailScreen: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const route = useRoute<ResourceDetailRouteProp>();
  const navigation = useNavigation();
  const { resource } = route.params;

  // Set navigation title like iOS (resource.name becomes the title)
  React.useEffect(() => {
    const title = resource.name || resource.title || 'Resource';
    navigation.setOptions({ title });
  }, [navigation, resource]);

  const handleUrlPress = async (url: string) => {
    const ok = await openExternalUrl(url);
    if (!ok) showAlert('Error', 'Cannot open this URL');
  };

  const [imageViewer, setImageViewer] = React.useState<{ visible: boolean; uri: string | null }>({
    visible: false,
    uri: null,
  });

  const handleOpenImage = (file: ICommunityResourceFile) =>
    setImageViewer({ visible: true, uri: file.fileUrl });

  const handleCloseImage = () =>
    setImageViewer({ visible: false, uri: null });

  const handleOpenPdf = (file: ICommunityResourceFile) => {
    (navigation as any).navigate('PdfScreen', {
      url: file.fileUrl,
      title: file.label || `File ${file.id}`,
    });
  };

  const iconName = getCommunityResourceIcon(resource.icon || resource.iconName);
  const title = resource.name || resource.title || '';
  const description = resource.description || '';

  const renderContentSection = (sectionTitle: string, content: React.ReactNode) => (
    <View style={styles.contentSection}>
      <Text style={styles.contentSectionTitle}>{sectionTitle}</Text>
      {content}
    </View>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Main Details Card */}
      <View style={styles.detailsCard}>
        <View style={[styles.icon, { backgroundColor: CommunityResourceIconColors.secondaryBackground }]}>
          <Icon 
            name={iconName} 
            size={ICON_SIZES.large} 
            color={CommunityResourceIconColors.primary} 
          />
        </View>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.description}>{description}</Text>
      </View>

      {/* Information Section */}
      {resource.content && (
        renderContentSection(
          homeStrings.communityResources.detail.info.title,
          <Text style={styles.contentText}>{resource.content}</Text>
        )
      )}

      {/* Website Section */}
      {resource.url && (
        renderContentSection(
          homeStrings.communityResources.detail.website.title,
          <TouchableOpacity onPress={() => handleUrlPress(resource.url!)}>
            <Text style={styles.linkText}>{resource.url}</Text>
          </TouchableOpacity>
        )
      )}

      {/* Files Section */}
      {resource.files && resource.files.length > 0 && (
        renderContentSection(
          homeStrings.communityResources.detail.files.title,
          <View style={styles.filesContainer}>
            {resource.files.map((file: ICommunityResourceFile) => (
              <FileRow
                key={file.id}
                file={file}
                onOpenImage={handleOpenImage}
                onOpenPdf={handleOpenPdf}
              />
            ))}
          </View>
        )
      )}
      <ImageViewerModal
        visible={imageViewer.visible}
        images={imageViewer.uri ? [imageViewer.uri] : []}
        onClose={handleCloseImage}
      />
    </ScrollView>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  content: {
    padding: PADDING.padding4,
  },
  detailsCard: {
    ...CARD_STYLES,
    backgroundColor: colors.card.bg,
    padding: PADDING.padding4,
    alignItems: 'flex-start',
    marginBottom: PADDING.padding4,
  },
  icon: {
    width: ICON_SIZES.large,
    height: ICON_SIZES.large,
    borderRadius: ICON_SIZES.large / 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: PADDING.padding2,
  },
  title: {
    ...TYPOGRAPHY.sectionTitle,
    color: colors.text.primary,
    marginBottom: PADDING.paddingHalf,
  },
  description: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.muted,
    fontSize: 14,
  },
  contentSection: {
    marginBottom: PADDING.padding4,
  },
  contentSectionTitle: {
    ...TYPOGRAPHY.cardTitle,
    color: colors.text.muted,
    fontSize: 13,
    marginBottom: PADDING.padding2,
  },
  contentText: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.primary,
    fontSize: 13,
  },
  linkText: {
    ...TYPOGRAPHY.cardDescription,
    color: '#007AFF', // iOS link color
    fontSize: 14,
    textDecorationLine: 'none', // iOS doesn't underline by default
  },
  filesContainer: {
    gap: PADDING.padding1,
  },
  fileItem: {
    marginBottom: PADDING.padding1,
  },
});